import React from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../layouts/TemplateColumn';
import { PageHeader, PageSection, RelatedContent, CrossLink, IndicationPageIntro, AnchorLinks, PrimaryClaim, SecondaryCallout, KMCurve, BoxedDefinitionList, ReferencesBlock, NCCNcallout, PageSubSection } from '../../components';
import { StaticImage } from 'gatsby-plugin-image';
import ARCC from '../../components/StudyDesign/__study-design/arcc'
import { jobCodes } from '../../util/data/jobCodes';
import styled from 'styled-components';
import * as colors from '../../util/colors';
import { Heading } from '../../util/typography';
import { createMarkup } from '../../util/functions';

// SEO INFORMATION
const pageMeta = {
    title: `KEYNOTE-564 - Clinical Findings & Study Design`,
    keywords: `keynote 564, keynote-564`,
    description: `Health care professionals may find clinical trial results from KEYNOTE-564 for the adjuvant treatment of certain patients with renal cell carcinoma (RCC).`,
    schemaJsonLD: [
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/adjuvant-renal-cell-carcinoma/","@type":"ApprovedIndication","name":"KEYTRUDA® and and KEYTRUDA QLEX™","alternateName":"pembrolizumab and berahyaluronidase alfa-pmph","description":"KEYTRUDA and KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph) Injection 165 mg + 2,000 units/mL are each indicated for the adjuvant treatment of adult patients with renal cell carcinoma (RCC) at intermediate-high or high risk of recurrence following nephrectomy, or following nephrectomy and resection of metastatic lesions."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/adjuvant-renal-cell-carcinoma/","@type":"MedicalAudience","audienceType":"US Health Care Professionals","geographicArea":"This site is intended for health care professionals of the United States, its territories, and Puerto Rico."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/adjuvant-renal-cell-carcinoma/","@type":"MedicalWebPage","name":"Adjuvant Treatment for Renal Cell Carcinoma (RCC)","description":"The effectiveness of KEYTRUDA QLEX for its approved indications has been established based upon evidence from the adequate and well-controlled studies conducted with KEYTRUDA and additional data comparing the pharmacokinetic, efficacy, and safety profiles of KEYTRUDA QLEX and KEYTRUDA."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/adjuvant-renal-cell-carcinoma/","@type":"MedicalTrial","name":"KEYNOTE-564","description":"A landmark, Phase 3, multicenter, randomized, double-blind, placebo-controlled trial in 994 patients.","trialDesign":"MultiCenterTrial"}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/adjuvant-renal-cell-carcinoma/","@type":"MedicalGuideline","guideline":"Pembrolizumab (KEYTRUDA®) is the ONLY NCCN Category 1 RECOMMENDED adjuvant treatment for certain patients with Stage II and Stage III ccRCC post nephrectomy","recognizingAuthority":"NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®)"}`
    ]
}

//from /src/util/data/indications.js
const indicationId = 29;
const jobCode = jobCodes[8].jobCode;

const relatedContent = [
    {
        section: 'Resources',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action'
        }
    },
    {
        section: 'Resources',
        link: {
            url: 'https://www.merckaccessprogram-keytruda.com/',
            label: 'Access & Reimbursement Support'
        }
    }
];

const crossLinkContent = {
    sections: [
        {
            heading: "Related Information",
            items: [
                { text: 'Selected Adverse Reactions', url: '/safety/adverse-reactions/adjuvant-renal-cell-carcinoma/' },
                { text: 'Dosing', url: '/dosing/options/#rcc' },
            ]
        },
    ],
    interestLinks: {
        heading: "You Might Be Interested In",
        items: [
            { eyebrow: 'Resources', text: 'Mechanism of Action', url: '/resources/mechanism-of-action/' },
        ],
    }
};

const KMofDFSFootnotes = [
    {
        label: "a.",
        text: "Based on the stratified Cox proportional hazard model."
    },
    {
        label: "b.",
        text: "Based on stratified log-rank test."
    },
    {
        label: "c.",
        text: "<i>P</i> value (one-sided) is compared with a boundary of 0.0114."
    },
]

const KMofDFSFootnotesNew = [
    {
        label: "d.",
        text: "Based on the stratified Cox proportional hazard model."
    },
    {
        label: "e.",
        text: "Based on stratified log-rank test."
    },
    {
        label: "f.",
        text: "<i>P</i> value (one-sided) is compared with a boundary of 0.0072."
    },
]

const boxedFootnoteList = [
    {
        label: "i.",
        text: "Patients with metastatic disease who had undergone complete resection of primary and metastatic lesions."
    },
]

const boxedDefinitions = `T2 = tumor >7 cm in greatest dimension, limited to the kidney; M0 = no distant metastasis; T4 = tumor invades beyond Gerota’s fascia (including contiguous extension into the ipsilateral adrenal gland); N1 = metastasis in regional lymph node(s).`;

const patientInclusionCriteriaItems = [
    {
        header: "Intermediate-high risk of recurrence",
        description: `<p>pT2 with Grade 4 only, N0, M0</p>
                      <p>pT3, any grade, N0, M0</p>
        `
    },
    {
        header: "High risk of recurrence",
        description: `<p>pT4, any grade, N0, M0</p>
                      <p>Any pT, any grade, N1, M0</p>
        `
    },
    {
        header: "M1 NED",
        description: `<p>M1 no evidence of disease<sup>i</sup></p>`
    },
]

const NCCNcalloutFootnotes = [
    {
        label: "j.",
        text: "See the NCCN Guidelines® for detailed recommendations, including other treatment options.<sup>5</sup>"
    },
    {
        label: "k.",
        text: "Pembrolizumab and berahyaluronidase alfa-pmph subcutaneous injection (KEYTRUDA QLEX) may be substituted for IV pembrolizumab (KEYTRUDA). Pembrolizumab and berahyaluronidase alfa-pmph has different dosing and administration instructions compared to IV pembrolizumab."
    }
]

const NCCNcalloutdefinitions = `
        <p>Category 1 = Based upon high-level evidence (≥1 randomized phase 3 trials or high-quality, robust meta-analyses), there is uniform NCCN consensus (≥85% support of the Panel) that the intervention is appropriate.<sup>5</sup></p>
        <p>Category 2A = Based upon lower-level evidence, there is uniform NCCN consensus (≥85% support of the Panel) that the intervention is appropriate.<sup>5</sup></p>
        <p>NCCN makes no warranties of any kind whatsoever regarding their content, use, or application and disclaims any responsibility for their application or use in any way.<sup>5</sup></p>
        <p>ccRCC = clear cell renal cell carcinoma.</p>
        `;

const NCCNcalloutData = {
    title: 'Pembrolizumab (KEYTRUDA®) is the <b>ONLY NCCN Category 1</b> National Comprehensive Cancer Network® (NCCN®) RECOMMENDED adjuvant systemic therapy option for certain patients with <b>Stage II and Stage III</b> ccRCC post nephrectomy<sup>5,j,k</sup>',
    showBadge: false,
    bodyCopy: `
      
        <ul>
            <li>NCCN CATEGORY 1—Stage II ccRCC with Grade 4 tumors +/- sarcomatoid features<sup style="margin-left: 1px;">j</sup></li>
            <li>NCCN CATEGORY 1—Stage III ccRCC<sup style="margin-left: 1px;">j</sup></li>
            <li>NCCN CATEGORY 2A—Stage IV ccRCC with resectable T4 tumors without distant metastasis (T4, M0) post nephrectomy<sup style="margin-left: 1px;">j</sup></li>
            <li>NCCN CATEGORY 2A—Stage IV ccRCC after metastasectomy with complete resection of disease within 1 year of nephrectomy<sup style="margin-left: 1px;">j</sup></li>
        </ul>
    `,
}

const NCCNcalloutReferences = [
    {
        label: "5.",
        text: `Referenced with permission from the NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®) for Kidney Cancer V.1.2027. © National Comprehensive Cancer Network, Inc. 2026. All rights reserved. Accessed July 1, 2026. To view the most recent and complete version of the guidelines, go online to NCCN.org.`
    },
];


const pageReferences = [
    {
        label: "1.",
        text: "Choueiri TK, Tomczak P, Park SH, et al; for the KEYNOTE&#8288;-&#8288;564 investigators. Adjuvant pembrolizumab after nephrectomy in renal-cell carcinoma. <i>N Engl J Med.</i> 2021;385(8):683–694."
    },  
     {
        label: "2.",
        text: `Haas N. Five-year follow-up results from the phase 3 KEYNOTE-564 study of adjuvant pembrolizumab for the treatment of clear cell renal cell carcinoma. Presented at: American Society of Clinical Oncology Annual Meeting; May 30–June 3, 2025; Chicago, IL.`
    }, 
    {
        label: "3.",
        text: `Choueiri TK, Tomczak P, Park SH, et al. Overall survival with adjuvant pembrolizumab in renal-cell carcinoma. <i>N Engl J Med.</i> 2024;390:1359-1371.`
    },
    {
        label: "4.",
        text: `Powles T, Tomczak P, Park SH, et al. Pembrolizumab versus placebo as post-nephrectomy adjuvant therapy for clear cell renal cell carcinoma (KEYNOTE&#8288;-&#8288;564): 30-month follow-up analysis of a multicentre, randomised, double-blind, placebo-controlled, phase 3 trial. <i>Lancet Oncol.</i> 2022;23(9):1133-1144. doi: 10.1016/S1470-2045(22)00487-9`
    },
    {
        label: "5.",
        text: `Referenced with permission from the NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®) for Kidney Cancer V.1.2027. © National Comprehensive Cancer Network, Inc. 2026. All rights reserved. Accessed July 1, 2026. To view the most recent and complete version of the guidelines, go online to NCCN.org.`
    },
]




const anchorLinkModelData = [
    {label: 'Clinical Findings from KEYNOTE&#8288;-&#8288;564 (DFS and OS) for KEYTRUDA'},
    { label: 'Study Design for KEYNOTE&#8288;-&#8288;564 for KEYTRUDA' },
    { label: 'Select NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®) for Kidney Cancer<sup>5</sup>' }
]

const PageSectionTitle = [
    { label: 'KEYNOTE&#8288;-&#8288;564: DFS at First Interim Analysis<sup>1</sup>'},
    { label: 'KEYTRUDA: 5-Year Follow-Up of DFS at Fourth Interim Analysis in KEYNOTE-564<sup>2</sup>' },
    { label: 'KEYNOTE&#8288;-&#8288;564: OS at Third Interim Analysis<sup>3</sup>' },
    { label: 'KEYTRUDA: 5-Year Follow-Up of OS at Fourth Interim Analysis in KEYNOTE-564<sup>2</sup>' },
    { label: 'Patient Inclusion Criteria<sup>1</sup>'},
]

const StyledSubText = styled.div`
    display: block;
    margin-top: -30px !important;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    color: ${colors.charcoal};
`;

const StyledSubText2 = styled(StyledSubText)`
    margin-top: 10px !important;
`;

const Page = ({ location }) => {

    return (
        <DefaultLayout indicationId={indicationId} jobCode={jobCode} pageMeta={pageMeta} location={location}>
            <div data-template-name="template-e">

                {/* Begin Intro Column */}
                <TemplateColumn id="template-e-intro" maxWidth={824}>
                    <PageHeader brand="keytrudaIV" batch2 title="Clinical Trial Results" subtitle="For KEYTRUDA" />
                    <PageSection bgColor="white">
                        <IndicationPageIntro indicationId={indicationId} />
                        <AnchorLinks items={anchorLinkModelData} />
                    </PageSection>
                    <PageSection bgColor="gradient">
                        <PrimaryClaim theme="light">KEYTRUDA is the ONLY APPROVED anti&#8288;–&#8288;PD&#8288;-&#8288;1 immunotherapy to demonstrate both DFS and OS benefit vs placebo in the adjuvant setting for patients with RCC at increased risk of recurrence post nephrectomy</PrimaryClaim>
                    </PageSection>
                </TemplateColumn>
                {/* End Intro Column */}

                <TemplateColumnWithSidebarContainer>
                    {/* Begin Main Column */}
                    <TemplateColumn id="template-e-main">

                        <PageSection bgColor="cloud" title={anchorLinkModelData[0].label}>
                            <Heading.H2 dangerouslySetInnerHTML={createMarkup(PageSectionTitle[0].label)} />
                            <StyledSubText2>(Median Follow-Up: 24.1 Months [Range: 14.9&ndash;41.5 Months])</StyledSubText2>

                            <SecondaryCallout 
                                title="Superior DFS with KEYTRUDA vs placebo" 
                                text="HR<sup>a</sup>=0.68 (95% CI, 0.53–0.87); <i>P</i><sup>b</sup>=0.0010<sup>c</sup>" 
                                alt=""    
                            />

                            <KMCurve
                                title="Kaplan-Meier Estimates of DFS in KEYNOTE&#8288;-&#8288;564<sup>1</sup>"
                                limitation="Median time from randomization to data cutoff: 24.1 months (range, 14.9–41.5)"
                                hr="32% reduction in the risk of disease recurrence, metastasis, or death with KEYTRUDA vs placebo; HR<sup>a</sup>=0.68 (95% CI, 0.53–0.87); <i>P</i><sup>b</sup>=0.0010<sup>c</sup>"
                                footnotes={KMofDFSFootnotes}
                                definitions="DFS = disease-free survival; OS = overall survival; NCCN® = National Comprehensive Cancer Network®; HR = hazard ratio; CI = confidence interval."
                                additionalInformation={[
                                    {
                                        title: 'Additional Information',
                                        text: `
                                            <p>Events observed: 109/496 (22%) with KEYTRUDA vs 151/498 (30%) with placebo.</p>
                                            <p>Median DFS: Not reached for KEYTRUDA or placebo.</p>`
                                           
                                    },
                                ]}  
                                image={() => <StaticImage 
                                    src="../../assets/arcc-564-km-v5.png" 
                                    placeholder="blurred" 
                                    alt="KEYNOTE-564 Kaplan-Meier Estimates of DFS for the First Interim Analysis in the Adjuvant Treatment of Patients With Renal Cell Carcinoma at Increased Risk of Recurrence" />} 
                            />


                        </PageSection>

                        <PageSection title={PageSectionTitle[1].label}>
                            <StyledSubText>(Median Follow-Up: 69.5 Months [range: 60.2&ndash;86.9 months])</StyledSubText>
                            <SecondaryCallout 
                                title="LIMITATIONS: Statistical testing for DFS was completed at First Interim Analysis, and, therefore, no formal statistical testing was performed at Fourth Interim Analysis. No conclusions can be drawn." 
                                alt=""    
                            />

                            <KMCurve
                                title="Kaplan-Meier Estimates of DFS in KEYNOTE&#8288;-&#8288;564<sup>2</sup>"
                                limitation="Median time from randomization to data cutoff: 69.5 months (range: 60.2–86.9 months)"
                                hr="HR=0.71 (95% CI, 0.59–0.86)"
                                copyrightAttribution="From <i>N Engl J Med</i>, Choueiri TK, Tomczak P, Park SH, et al., Overall survival with adjuvant pembrolizumab in renal-cell carcinoma, 390., 1359–1371. Copyright ©2024 Massachusetts Medical Society. Reprinted with permission from Massachusetts Medical Society."
                                
                                additionalInformation={[
                                    {
                                        title: 'Additional Information',
                                        text: `<p>Events observed: 188/496 (38%) with KEYTRUDA vs 241/498 (48%) with placebo.<sup>2</sup></p>
                                            <p>Median DFS: Not reached for KEYTRUDA vs 68.3 months (51.7–not reached) for placebo.<sup>2</sup></p>`

                                    },
                                ]}  
                                image={() => <StaticImage 
                                    src="../../assets/arcc-564-km-followup-v5.png" 
                                    placeholder="blurred" 
                                    alt="KEYNOTE-564 Kaplan-Meier Estimates 5-Year Follow-up DFS for the Fourth Interim Analysis in the Adjuvant Treatment of Certain Patients With Renal Cell Carcinoma" />} 
                                    chartAttribution="From the American Society of Clinical Oncology Annual Meeting; May 30-June 3, 2025; Chicago, IL."
                            />
                       
                        </PageSection>


                        {/* ITEM 39 */}
                        <PageSection bgColor="cloud">
                             <SecondaryCallout 
                                title="KEYTRUDA is the ONLY APPROVED anti–PD-1 immunotherapy to demonstrate an OS BENEFIT vs placebo for patients with RCC at increased risk of recurrence post nephrectomy" 
                                alt=""    
                                // footnotes={secondaryCalloutFootnote}
                            />
                             <Heading.H2 dangerouslySetInnerHTML={createMarkup(PageSectionTitle[2].label)} />
                            <StyledSubText2>(Median Follow-Up: 57.2 Months [Range: 47.9&ndash;74.5 Months])<sup>3</sup></StyledSubText2>

                            {/* <PageSubSection> */}
                            <SecondaryCallout 
                                title="Superior OS with KEYTRUDA vs placebo" 
                                alt=""    
                                text="HR<sup>d</sup>=0.62 (95% CI, 0.44–0.87); <i>P</i><sup>e</sup>=0.0024<sup>f</sup>"
                            />
                            <KMCurve
                                title="Kaplan-Meier Estimates of OS in KEYNOTE&#8288;-&#8288;564<sup>3</sup>"
                                limitation="Median time from randomization to data cutoff: 57.2 months (range: 47.9–74.5 months)"
                                hr="38% reduction in the risk of death with KEYTRUDA vs placebo; HR<sup>d</sup>=0.62 (95% CI, 0.44–0.87); <i>P</i><sup>e</sup>=0.0024<sup>f</sup>"
                                footnotes={KMofDFSFootnotesNew}
                                additionalInformation={[
                                    {
                                        title: 'Additional Information',
                                        text: `<p>Events observed: 55/496 (11%) with KEYTRUDA vs 86/498 (17%) with placebo.</p>
                                            <p>Median OS: Not reached for KEYTRUDA or placebo.</p>`
                                    },
                                ]} 
                               
                                image={() => <StaticImage 
                                    src="../../assets/km-rcc-564-v2.png" 
                                    placeholder="blurred" 
                                    alt="KEYNOTE-564 Kaplan-Meier Estimates of Follow-up OS for the Third Interim Analysis in the Adjuvant Treatment of Patients With Renal Cell Carcinoma at Increased Risk of Recurrence" />} 
                            />                             
                            
                            {/* </PageSubSection> */}
                        </PageSection>
                        <PageSection bgColor="cloud" title={PageSectionTitle[3].label}>
                            <StyledSubText>(Median Follow-Up: 69.5 Months [Range: 60.2&ndash;86.9 Months])</StyledSubText>                                                      
                            <SecondaryCallout 
                                title="LIMITATIONS: Statistical testing for OS was completed at Third Interim Analysis, and, therefore, no formal statistical testing was performed at Fourth Interim Analysis. No conclusions can be drawn." 
                                alt=""    
                            />
                            <KMCurve
                                title="Kaplan-Meier Estimates of OS in KEYNOTE&#8288;-&#8288;564<sup>2</sup>"
                                limitation="Median time from randomization to data cutoff: 69.5 months (range: 60.2–86.9 months)"
                                hr="HR=0.66 (95% CI, 0.48–0.90)"
                                additionalInformation={[
                                    {
                                        title: 'Additional Information',
                                        text: `<p>Events observed: 68/496 (14%) with KEYTRUDA vs 99/498 (20%) with placebo.<sup>2</sup></p>
                                            <p>Median OS: Not reached for KEYTRUDA or placebo.<sup>2</sup></p>`
                                    },
                                ]} 
                        
                               
                                image={() => <StaticImage 
                                    src="../../assets/arcc-564-os4-km.png" 
                                    placeholder="blurred" 
                                    alt="KEYNOTE-564 Kaplan-Meier Estimates of Follow-up OS for the Fourth Interim Analysis in the Adjuvant Treatment of Certain Patients With Renal Cell Carcinoma" />} 
                                 chartAttribution="From the American Society of Clinical Oncology Annual Meeting; May 30-June 3, 2025; Chicago, IL."   
                            />
                            
                        </PageSection>
                        <PageSection title={anchorLinkModelData[1].label}>
                            <ARCC />
                        </PageSection>
                        
                        <PageSection title={PageSectionTitle[4].label}>
                            <BoxedDefinitionList 
                                items={patientInclusionCriteriaItems}
                                footnotes={boxedFootnoteList}
                                definitions={boxedDefinitions}
                            />
                        </PageSection>

                        <PageSection bgColor="cloud" title={anchorLinkModelData[2].label}>
                            <NCCNcallout
                                data={NCCNcalloutData}
                                footnotes={NCCNcalloutFootnotes}
                                definitions={NCCNcalloutdefinitions}
                                references={NCCNcalloutReferences}
                            />

                            <SecondaryCallout 
                                title='All patients with non-metastatic T3 tumors, regardless of nodal status, are eligible for adjuvant treatment with KEYTRUDA after nephrectomy<sup>1</sup>'
                                alt=""  
                            />
                        </PageSection>
                    </TemplateColumn>

                    {/* End Main Column */}
                    <TemplateColumn id="template-e-sidebar">
                        <PageSection bgColor="mist">
                            <CrossLink {...crossLinkContent} />
                        </PageSection>
                    </TemplateColumn>
                </TemplateColumnWithSidebarContainer>

                <TemplateColumn id="template-e-secondary">
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent} />
                    </PageSection>

                    <PageSection bgColor="mist">
                        <ReferencesBlock references={pageReferences} />
                    </PageSection>
                </TemplateColumn>
            </div>
        </DefaultLayout >
    );
};

export default Page;
